import { defineStore } from 'pinia';
import {
  ref, computed, watch,
} from 'vue';
import { v4 as uuidv4 } from 'uuid';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export const useSAM2Store = defineStore('sam2', () => {
  const servers = ref(null);
  const sam2Server = ref(null);
  const sam2ServerIp = computed(() => (sam2Server.value ? sam2Server.value.ip : null));
  const fetchingCloudInstances = ref(false);
  const serverHealthCheckedOnce = ref(false);
  const isSam2ServerFound = ref(false);
  const isSam2ServerAvailable = ref(false);
  const serverTimeElapsed = ref(-1);
  const sam2Options = ref({
    mode: 'drag_box',
    params: {
      pointType: 'box',
      clicks: [],
      box: [],
      boxes: [],
    },
  });
  const sam2Annotations = ref([]);
  const sam2LastPropagatedAnnotations = ref([]);
  const aigt3dAnnotations = ref([]);
  const aigt3dLastPropagatedAnnotations = ref([]);
  const aigt2dAnnotations = ref([]);
  const aigt2dLastPropagatedAnnotations = ref([]);
  const isSAM2ClickMode = computed(() => sam2Options.value?.mode === 'hover_and_click');
  const isSAM2BoxMode = computed(() => sam2Options.value?.mode === 'drag_box');
  const canAnnotate = computed(() => selectedObject.value !== null && hasInitState.value);

  // const clicksByObjId = computed(() => Object.groupBy(sam2Options.value.params.clicks, ({ obj_id }) => obj_id));
  // const boxByObjId = computed(() => Object.groupBy(sam2Options.value.params.box, ({ obj_id }) => obj_id));

  const isInitializingState = ref(false);
  const hasInitState = ref(false);
  const initStatus = ref('');
  const initDetail = ref('');
  const initProgress = ref(0);
  const initTotal = ref(0);
  const initError = ref('');
  const initStartFrame = ref(1);
  const initEndFrame = ref(2);
  const initEndFrameReverse = ref(1);
  const isPropagating = ref(false);
  const propagateStatus = ref('');
  const propagateDetail = ref('');
  const propagateProgress = ref(0);
  const propagateTotal = ref(0);
  const shouldClearNextPoints = ref(false);
  const isCreatingAnnotations = ref(false);
  const propagateLastAnnotationIndex = ref(0);
  const reversePropagateEnabled = ref(false);
  const objects = ref([]);
  const objectsLabelMap = ref({});
  const objectsBoxMap = ref({});
  const selectedObject = ref(null);
  const isAddingNewPointsOrBox = ref(false);
  const isCancellingPropagation = ref(false);

  function $reset() {
    servers.value = null;
    sam2Server.value = null;
    fetchingCloudInstances.value = false;
    serverHealthCheckedOnce.value = false;
    isSam2ServerFound.value = false;
    isSam2ServerAvailable.value = false;
    serverTimeElapsed.value = -1;
    sam2Options.value = {
      mode: 'drag_box',
      params: {
        pointType: 'box',
        clicks: [],
        box: [],
        boxes: [],
      },
    };
    sam2Annotations.value = [];
    sam2LastPropagatedAnnotations.value = [];
    aigt3dAnnotations.value = [];
    aigt3dLastPropagatedAnnotations.value = [];
    aigt2dAnnotations.value = [];
    aigt2dLastPropagatedAnnotations.value = [];

    isInitializingState.value = false;
    hasInitState.value = false;
    initStatus.value = '';
    initDetail.value = '';
    initProgress.value = 0;
    initTotal.value = 0;
    initError.value = '';
    initStartFrame.value = 1;
    initEndFrame.value = 2;
    initEndFrameReverse.value = 1;
    isPropagating.value = false;
    propagateStatus.value = '';
    propagateDetail.value = '';
    propagateProgress.value = 0;
    propagateTotal.value = 0;
    shouldClearNextPoints.value = false;
    isCreatingAnnotations.value = false;
    propagateLastAnnotationIndex.value = 0;
    reversePropagateEnabled.value = false;
    objects.value = [];
    objectsLabelMap.value = {};
    objectsBoxMap.value = {};
    selectedObject.value = null;
    isCancellingPropagation.value = false;
  }

  function $resetServer() {
    isSam2ServerFound.value = false;
    isSam2ServerAvailable.value = false;
    fetchingCloudInstances.value = false;
    serverHealthCheckedOnce.value = false;
    serverTimeElapsed.value = -1;
    sam2Options.value = {
      mode: "drag_box",
      params: {
        pointType: 'box',
        clicks: [],
        box: [],
        boxes: [],
      },
    };
    sam2Annotations.value = [];
    sam2LastPropagatedAnnotations.value = [];
    aigt3dAnnotations.value = [];
    aigt3dLastPropagatedAnnotations.value = [];
    aigt2dAnnotations.value = [];
    aigt2dLastPropagatedAnnotations.value = [];

    isInitializingState.value = false;
    hasInitState.value = false;
    initStatus.value = '';
    initDetail.value = '';
    initProgress.value = 0;
    initTotal.value = 0;
    initError.value = '';
    // initStartFrame.value = 1;
    // initEndFrame.value = 2;
    isPropagating.value = false;
    propagateStatus.value = '';
    propagateDetail.value = '';
    propagateProgress.value = 0;
    propagateTotal.value = 0;
    shouldClearNextPoints.value = false;
    isCreatingAnnotations.value = false;
    propagateLastAnnotationIndex.value = 0;
    reversePropagateEnabled.value = false;
    objects.value = [];
    objectsLabelMap.value = {};
    objectsBoxMap.value = {};
    selectedObject.value = null;
    isCancellingPropagation.value = false;
  }

  function handleSAM2ImageClick(e, x, y, imageId, frame) {
    if (sam2Options.value.params.pointType !== 'add' && sam2Options.value.params.pointType !== 'minus') {
      return;
    }

    if (canAnnotate.value && (e.evt.button === 0 || e.evt.button === 2)) {
      e.evt.preventDefault();
      let clickType;
      if (e.evt.button === 0) {
        if (sam2Options.value.params.pointType === 'add') {
          clickType = 1;
        } else if (sam2Options.value.params.pointType === 'minus') {
          clickType = 0;
        }
      } else {
        clickType = 0;
      }
      const newSAMOptions = { ...sam2Options.value };
      newSAMOptions.params.clicks.push({
        x, y, clickType, imageId, frame_idx: frame - initStartFrame.value, obj_id: selectedObject.value,
      });
    }
  }

  function getFrameSAM2Annotations(frame_image_id) {
    if (!frame_image_id) return [];
    return sam2Annotations.value.filter((anno) => anno.image_id === frame_image_id);
  }

  function getFrameAndObjectSAM2LastPropagatedAnnotations(frame_image_id, obj_id) {
    if (!frame_image_id || !obj_id) return [];
    return sam2LastPropagatedAnnotations.value.filter((anno) => anno.image_id === frame_image_id && anno.object_reference === obj_id);
  }

  function getFrameAndObjectAigt3dLastPropagatedAnnotations(frame_image_id, obj_id) {
    if (!frame_image_id || !obj_id) return [];
    return aigt3dLastPropagatedAnnotations.value.filter((anno) => anno.image_id === frame_image_id && anno.object_reference === obj_id);
  }

  function getFrameAndObjectAigt2dLastPropagatedAnnotations(frame_image_id, obj_id) {
    if (!frame_image_id || !obj_id) return [];
    return aigt2dLastPropagatedAnnotations.value.filter((anno) => anno.image_id === frame_image_id && anno.object_reference === obj_id);
  }

  async function getElapsedServerTime() {
    if (!sam2Server.value || !sam2Server.value?.launch) {
      serverTimeElapsed.value = -1;
    }
    const launchTime = new Date(sam2Server.value.launch);
    const dataConnect = new DatastoreConnect();
    await dataConnect.getElapsedTime({ start_time: launchTime })
      .then((resp) => {
        if (resp.result) {
          serverTimeElapsed.value = Math.floor(resp.result.seconds);
        }
      });
  }

  async function listCloudInstances() {
    fetchingCloudInstances.value = true;
    const dataConnect = new DatastoreConnect();
    return dataConnect.listCloudInstances({ task_types: ["aigt"], is_user_initiated: true })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error);
        }
        fetchingCloudInstances.value = false;
        return resp.result;
      })
      .catch((error) => {
        fetchingCloudInstances.value = false;
        throw error;
      });
  }

  function handleSAMAddObject() {
    const uuid = uuidv4();
    sam2Options.value.mode = 'drag_box';
    sam2Options.value.params.pointType = 'box';
    objects.value.push(uuid);
    selectedObject.value = uuid;
  }

  async function handleSAMReset() {
    propagateStatus.value = '';
    propagateDetail.value = '';
    propagateProgress.value = 0;
    propagateTotal.value = 0;

    // Reset SAM2 Controls
    sam2Options.value.mode = 'drag_box';
    sam2Options.value.params.pointType = 'box';
    sam2Options.value.params.clicks = [];
    sam2Options.value.params.box = [];
    sam2Options.value.params.boxes = [];
    objects.value = [];
    selectedObject.value = null;
    handleSAMAddObject();
    // Reset AIGT annotations
    sam2Annotations.value = [];
    aigt3dAnnotations.value = [];
    aigt2dAnnotations.value = [];
    // Reset SAM2 State
  }

  async function handleResetSAMState() {
    // Sending a POST request
    return fetch(`aigt/reset_state?ip=${sam2ServerIp.value}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error resetting state`);
        }
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  }

  return {
    servers,
    sam2Server,
    sam2ServerIp,
    fetchingCloudInstances,
    serverHealthCheckedOnce,
    isSam2ServerFound,
    isSam2ServerAvailable,
    serverTimeElapsed,
    sam2Options,
    sam2Annotations,
    sam2LastPropagatedAnnotations,
    aigt3dAnnotations,
    aigt3dLastPropagatedAnnotations,
    aigt2dAnnotations,
    aigt2dLastPropagatedAnnotations,
    isSAM2ClickMode,
    isSAM2BoxMode,
    canAnnotate,
    // clicksByObjId,
    // boxByObjId,
    isInitializingState,
    hasInitState,
    initStatus,
    initDetail,
    initProgress,
    initTotal,
    initError,
    initStartFrame,
    initEndFrame,
    initEndFrameReverse,
    isPropagating,
    propagateStatus,
    propagateDetail,
    propagateProgress,
    propagateTotal,
    propagateLastAnnotationIndex,
    shouldClearNextPoints,
    isCreatingAnnotations,
    reversePropagateEnabled,
    objects,
    objectsLabelMap,
    objectsBoxMap,
    selectedObject,
    isAddingNewPointsOrBox,
    isCancellingPropagation,
    handleSAMAddObject,
    handleSAMReset,
    handleResetSAMState,
    getElapsedServerTime,
    listCloudInstances,
    handleSAM2ImageClick,
    getFrameSAM2Annotations,
    getFrameAndObjectSAM2LastPropagatedAnnotations,
    getFrameAndObjectAigt3dLastPropagatedAnnotations,
    getFrameAndObjectAigt2dLastPropagatedAnnotations,
    $reset,
    $resetServer,
  };
});
